import type { ProductId } from "../lib/products";
import { apiBeacon, apiFetch, apiUpload, withQuery } from "./client";
import type {
  AdminArticle,
  AdminArticleRow,
  Agent,
  AgentSave,
  Article,
  ArticleAnalytics,
  ArticleSave,
  ArticleStats,
  ArticleTransition,
  BffUser,
  CategoryPage,
  CategorySave,
  DocsHome,
  LoginOutcome,
  MediaItem,
  MediaUploadResult,
  NavCategory,
  ReaderLookup,
  ReadEventResult,
  SearchHit,
  SectionSave,
  StructureCategory,
  StructureSave,
  WorkspaceMe,
  WorkspaceSettings,
} from "./types";

/**
 * Every path the app calls, in one file. The reader and console factories bind the product, so a
 * screen cannot make a partitioned call without one (the host refuses it: `unknown-product`).
 */

/** Sign-in through the support host's BFF (ADR-0046 decision 6). */
export const bffApi = {
  /** 401 when there is no session; issues the antiforgery cookie when there is. */
  user: () => apiFetch<BffUser>("/bff/user"),
  login: (login: string, password: string) =>
    apiFetch<LoginOutcome>("/bff/login", { method: "POST", body: { login, password } }),
  totp: (userId: string, code: string) =>
    apiFetch<LoginOutcome>("/bff/totp", { method: "POST", body: { userId, code } }),
  logout: () => apiFetch<void>("/bff/logout", { method: "POST" }),
};

/** Every call the reader-facing site makes: anonymous, same-origin `/kb/*?product=`. */
export function createDocsApi(product: ProductId) {
  const kb = (path: string, query: Record<string, string> = {}) =>
    withQuery(`/kb${path}`, product, query);

  return {
    home: (signal?: AbortSignal) => apiFetch<DocsHome>(kb("/home"), { signal }),

    nav: (signal?: AbortSignal) => apiFetch<NavCategory[]>(kb("/nav"), { signal }),

    category: (slug: string, signal?: AbortSignal) =>
      apiFetch<CategoryPage>(kb(`/categories/${encodeURIComponent(slug)}`), { signal }),

    /** A 403 with a gate body throws `RestrictedError`. */
    article: (slug: string, signal?: AbortSignal) =>
      apiFetch<Article>(kb(`/articles/${encodeURIComponent(slug)}`), { signal }),

    search: (query: string, signal?: AbortSignal) =>
      apiFetch<SearchHit[]>(kb("/search", { q: query }), { signal }),

    recordRead: (articleSlug: string, anonId: string) =>
      apiFetch<ReadEventResult>(kb("/read-events"), {
        method: "POST",
        body: { articleSlug, anonId, userRef: null, device: describeDevice() },
      }),

    /** Sent during pagehide, so it must survive the document going away. */
    reportDuration: (readEventId: number, durationSec: number) =>
      apiBeacon(kb(`/read-events/${readEventId}/beacon`), { durationSec }),

    assetUrl: (assetId: string) => kb(`/assets/${encodeURIComponent(assetId)}`),
  };
}

export type DocsApi = ReturnType<typeof createDocsApi>;

/** Every call the authoring console makes: `/api/kb/*?product=`, plus the unpartitioned team. */
export function createAdminApi(product: ProductId) {
  const api = (path: string, query: Record<string, string | undefined> = {}) =>
    withQuery(`/api/kb${path}`, product, query);

  return {
    me: () => apiFetch<WorkspaceMe>(api("/me")),

    listArticles: async (params: { status?: string; categoryId?: string; search?: string } = {}) =>
      (await apiFetch<unknown[]>(api("/articles", params))).map(toAdminArticleRow),

    getArticle: async (id: string) =>
      toAdminArticle(await apiFetch<unknown>(api(`/articles/${encodeURIComponent(id)}`))),

    previewArticle: (id: string) =>
      apiFetch<Article>(api(`/articles/${encodeURIComponent(id)}/preview`)),

    /** Answers the new article's id. */
    createArticle: (save: ArticleSave) =>
      apiFetch<string>(api("/articles"), { method: "POST", body: save }),

    updateArticle: (id: string, save: ArticleSave) =>
      apiFetch<string>(api(`/articles/${encodeURIComponent(id)}`), { method: "PUT", body: save }),

    transitionArticle: (id: string, transition: ArticleTransition) =>
      apiFetch<string>(api(`/articles/${encodeURIComponent(id)}/${transition}`), {
        method: "POST",
      }),

    deleteArticle: (id: string) =>
      apiFetch<string>(api(`/articles/${encodeURIComponent(id)}`), { method: "DELETE" }),

    getStructure: () => apiFetch<StructureCategory[]>(api("/structure")),

    saveStructure: (categories: StructureCategory[]) =>
      apiFetch<void>(api("/structure"), { method: "PUT", body: toStructureSave(categories) }),

    getRecommended: async () =>
      (await apiFetch<unknown[]>(api("/recommended"))).map(toAdminArticleRow),

    saveRecommended: (articleIds: string[]) =>
      apiFetch<void>(api("/recommended"), { method: "PUT", body: { articleIds } }),

    createCategory: (save: CategorySave) =>
      apiFetch<string>(api("/categories"), { method: "POST", body: save }),

    updateCategory: (id: string, save: CategorySave) =>
      apiFetch<string>(api(`/categories/${encodeURIComponent(id)}`), { method: "PUT", body: save }),

    deleteCategory: (id: string) =>
      apiFetch<string>(api(`/categories/${encodeURIComponent(id)}`), { method: "DELETE" }),

    createSection: (save: SectionSave) =>
      apiFetch<string>(api("/sections"), { method: "POST", body: save }),

    updateSection: (id: string, save: SectionSave) =>
      apiFetch<string>(api(`/sections/${encodeURIComponent(id)}`), { method: "PUT", body: save }),

    deleteSection: (id: string) =>
      apiFetch<string>(api(`/sections/${encodeURIComponent(id)}`), { method: "DELETE" }),

    listMedia: async (categoryId?: string) =>
      (await apiFetch<unknown[]>(api("/media", { categoryId }))).map(toMediaItem),

    /** Phase one: stage the bytes. Field name `files`, as the branch sent it. */
    uploadMedia: (files: File[]) => {
      const form = new FormData();
      files.forEach((file) => form.append("files", file));
      return apiUpload<MediaUploadResult>(api("/media/uploads"), form);
    },

    /** Phase two: move what was staged into the library folder. */
    commitMedia: async (stagingKeys: string[], categoryId: string | null) =>
      (
        await apiFetch<unknown[]>(api("/media/commit"), {
          method: "POST",
          body: { stagingKeys, categoryId },
        })
      ).map(toMediaItem),

    moveMedia: (id: string, categoryId: string | null) =>
      apiFetch<string>(api(`/media/${encodeURIComponent(id)}`), {
        method: "PATCH",
        body: { categoryId },
      }),

    deleteMedia: (id: string) =>
      apiFetch<string>(api(`/media/${encodeURIComponent(id)}`), { method: "DELETE" }),

    getSettings: () => apiFetch<WorkspaceSettings>(api("/settings")),

    saveSettings: (settings: WorkspaceSettings) =>
      apiFetch<void>(api("/settings"), { method: "PUT", body: settings }),

    articleStats: (range: { from?: string; to?: string } = {}) =>
      apiFetch<ArticleStats[]>(api("/analytics/articles", range)),

    articleAnalytics: (
      id: string,
      params: { from?: string; to?: string; tenantId?: string } = {},
    ) => apiFetch<ArticleAnalytics>(api(`/analytics/articles/${encodeURIComponent(id)}`, params)),

    readerLookup: (params: { tenantId?: string; userId?: string; from?: string; to?: string }) =>
      apiFetch<ReaderLookup>(api("/analytics/readers", params)),

    /**
     * The workspace members screen edits `support.agent` (ADR-0046 decision 7). Staff work across
     * every product, so `/api/agents` is not partitioned and takes no `?product=`.
     */
    listUsers: () => apiFetch<Agent[]>("/api/agents"),

    createUser: (save: AgentSave) =>
      apiFetch<string>("/api/agents", { method: "POST", body: save }),

    updateUser: (id: string, save: AgentSave) =>
      apiFetch<string>(`/api/agents/${encodeURIComponent(id)}`, { method: "PUT", body: save }),
  };
}

export type AdminApi = ReturnType<typeof createAdminApi>;

// ---- shape adapters ----
//
// Three console routes answer the host's service records rather than the branch-shaped records in
// KbConsoleContracts.cs: `GET /api/kb/articles` is `ArticleRow` (no effective rung, no author
// name), `GET /api/kb/articles/{id}` is `ArticleEditor` with the row nested, and `GET /api/kb/media`
// is `MediaRow` (a usage count, no titles). These adapters accept either shape, so the screens see
// one whichever the host sends; if the host moves to the contract records they shrink to casts.

type Loose = Record<string, unknown>;

const str = (value: unknown): string | null => (typeof value === "string" ? value : null);
const num = (value: unknown, fallback = 0): number =>
  typeof value === "number" ? value : fallback;

export function toAdminArticleRow(raw: unknown): AdminArticleRow {
  const r = raw as Loose;
  const own = str(r.minRole);
  return {
    id: String(r.id),
    slug: String(r.slug ?? ""),
    title: String(r.title ?? ""),
    sectionName: String(r.sectionName ?? ""),
    categoryName: String(r.categoryName ?? ""),
    status: (str(r.status) ?? "DRAFT") as AdminArticleRow["status"],
    effectiveMinRole: str(r.effectiveMinRole) ?? own ?? "ANONYMOUS",
    inheritsMinRole: typeof r.inheritsMinRole === "boolean" ? r.inheritsMinRole : own === null,
    authorName: str(r.authorName) ?? str(r.createdBy),
    lastUpdatedOn: str(r.lastUpdatedOn) ?? str(r.modifiedAt) ?? str(r.publishedAt),
    ...(str(r.sectionId) ? { sectionId: str(r.sectionId)! } : {}),
  };
}

export function toAdminArticle(raw: unknown): AdminArticle {
  const outer = raw as Loose;
  // `ArticleEditor(Row, ContentJson, ContentHtml, ParentArticleId)` nests the row.
  const r = (outer.row && typeof outer.row === "object" ? outer.row : outer) as Loose;
  return {
    id: String(r.id),
    sectionId: String(r.sectionId ?? ""),
    parentArticleId: str(outer.parentArticleId),
    slug: String(r.slug ?? ""),
    title: String(r.title ?? ""),
    summary: str(r.summary),
    contentJson: str(outer.contentJson),
    contentHtml: str(outer.contentHtml),
    status: (str(r.status) ?? "DRAFT") as AdminArticle["status"],
    minRole: str(r.minRole),
    sortOrder: num(r.sortOrder),
    authorName: str(r.authorName) ?? str(r.createdBy),
    lastUpdatedOn: str(r.lastUpdatedOn) ?? str(r.modifiedAt) ?? str(r.publishedAt),
  };
}

export function toMediaItem(raw: unknown): MediaItem {
  const r = raw as Loose;
  return {
    id: String(r.id),
    fileName: String(r.fileName ?? ""),
    contentType: String(r.contentType ?? "application/octet-stream"),
    sizeBytes: num(r.sizeBytes),
    categoryId: str(r.categoryId),
    categoryName: str(r.categoryName),
    createdOn: str(r.createdOn) ?? str(r.createdAt) ?? "",
    usedIn: Array.isArray(r.usedIn) ? (r.usedIn as string[]) : [],
    usedInCount: Array.isArray(r.usedIn) ? r.usedIn.length : num(r.usedInCount),
    assetPath: str(r.assetPath) ?? `/kb/assets/${String(r.id)}`,
  };
}

/** The GET tree carries more than the PUT reads; send only what `KbStructureSave` names. */
export function toStructureSave(categories: StructureCategory[]): StructureSave {
  return {
    categories: categories.map((category, ci) => ({
      id: category.id,
      name: category.name,
      sortOrder: ci,
      sections: category.sections.map((section, si) => ({
        id: section.id,
        name: section.name,
        sortOrder: si,
        articles: section.articles.map((article, ai) => ({
          id: article.id,
          title: article.title,
          sortOrder: ai,
        })),
      })),
    })),
  };
}

/**
 * A coarse description of the browser, matching the design's "Desktop · Chrome".
 *
 * Deliberately coarse: the analytics screens group by this, and a full user-agent string is both
 * unreadable in a table and more identifying than the feature needs.
 */
export function describeDevice(ua = navigator.userAgent): string {
  const platform = /Mobi|Android|iPhone/i.test(ua)
    ? "Mobile"
    : /iPad|Tablet/i.test(ua)
      ? "Tablet"
      : "Desktop";

  // Order matters: Edge and Chrome both claim "Chrome", Chrome claims "Safari".
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /OPR\//.test(ua)
      ? "Opera"
      : /Firefox\//.test(ua)
        ? "Firefox"
        : /Chrome\//.test(ua)
          ? "Chrome"
          : /Safari\//.test(ua)
            ? "Safari"
            : "Other";

  return `${platform} · ${browser}`;
}
